/**
 * StandaloneAccommodationDashboard - Accommodation management without events
 * For campus housing, guest rooms, dormitories
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building, BedDouble, Users, Plus, Search, RefreshCw, 
  CheckCircle, AlertTriangle, Wrench, Ban, Calendar,
  LogIn, LogOut, User, Phone, Mail, MapPin, Settings,
  Home, DoorOpen, Clock, TrendingUp, ArrowLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sub-components
import BuildingsManagement from './accommodation/BuildingsManagement';
import RoomsManagement from './accommodation/RoomsManagement';
import GuestsManagement from './accommodation/GuestsManagement';
import BookingsManagement from './accommodation/BookingsManagement';
import DamageReporting from './accommodation/DamageReporting';
import HousingStaffManager from './accommodation/HousingStaffManager';
import { getToken } from '@/lib/authToken';

const StandaloneAccommodationDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showQuickCheckIn, setShowQuickCheckIn] = useState(false);
  const [showQuickCheckOut, setShowQuickCheckOut] = useState(false);
  const [checkInForm, setCheckInForm] = useState({
    guest_name: '', phone: '', id_number: '', room_id: '', 
    check_out_date: '', purpose: 'guest'
  });
  const [selectedBookingForCheckOut, setSelectedBookingForCheckOut] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch accommodation stats
  const { data: stats = {}, isLoading: statsLoading } = useQuery({
    queryKey: ['accommodation-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/stats`, { headers });
      if (!res.ok) return {};
      return res.json();
    },
    refetchInterval: 30000
  });

  // Fetch rooms
  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ['accommodation-rooms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch active bookings
  const { data: activeBookings = [] } = useQuery({
    queryKey: ['active-bookings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings?status=active`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 30000
  });

  // Fetch checked-in bookings for checkout
  const { data: checkedInBookings = [] } = useQuery({
    queryKey: ['checked-in-bookings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings?status=checked_in`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 30000
  });

  // Quick check-in mutation (creates guest + booking + checks in)
  const quickCheckInMutation = useMutation({
    mutationFn: async (data) => {
      // First create the guest
      const guestRes = await fetch(`${API_URL}/api/admin/accommodation/guests`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          full_name: data.guest_name,
          phone: data.phone,
          id_number: data.id_number,
          purpose: data.purpose
        })
      });
      if (!guestRes.ok) throw new Error((await guestRes.json()).detail || 'Failed to create guest');
      const guest = await guestRes.json();

      // Then create the booking
      const today = new Date().toISOString().split('T')[0];
      const bookingRes = await fetch(`${API_URL}/api/admin/accommodation/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          guest_id: guest.id,
          room_id: data.room_id,
          check_in_date: today,
          check_out_date: data.check_out_date,
          purpose: data.purpose,
          notes: `Quick check-in`
        })
      });
      if (!bookingRes.ok) throw new Error((await bookingRes.json()).detail || 'Failed to create booking');
      const booking = await bookingRes.json();

      // Then check in
      const checkInRes = await fetch(`${API_URL}/api/admin/accommodation/bookings/${booking.id}/check-in`, {
        method: 'POST',
        headers
      });
      if (!checkInRes.ok) throw new Error((await checkInRes.json()).detail || 'Failed to check in');
      
      return booking;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-stats']);
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['active-bookings']);
      queryClient.invalidateQueries(['checked-in-bookings']);
      setShowQuickCheckIn(false);
      setCheckInForm({ guest_name: '', phone: '', id_number: '', room_id: '', check_out_date: '', purpose: 'guest' });
      toast.success('Guest checked in successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  // Quick check-out mutation
  const quickCheckOutMutation = useMutation({
    mutationFn: async (bookingId) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/bookings/${bookingId}/check-out`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ room_status: 'dirty' })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to check out');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-stats']);
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['active-bookings']);
      queryClient.invalidateQueries(['checked-in-bookings']);
      setShowQuickCheckOut(false);
      setSelectedBookingForCheckOut(null);
      toast.success('Guest checked out successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  const statusConfig = {
    ready: { color: 'bg-green-500', icon: CheckCircle, label: 'Ready', textColor: 'text-green-600' },
    occupied: { color: 'bg-blue-500', icon: Users, label: 'Occupied', textColor: 'text-blue-600' },
    dirty: { color: 'bg-yellow-500', icon: AlertTriangle, label: 'Dirty', textColor: 'text-yellow-600' },
    maintenance: { color: 'bg-red-500', icon: Wrench, label: 'Maintenance', textColor: 'text-red-600' },
    blocked: { color: 'bg-gray-500', icon: Ban, label: 'Blocked', textColor: 'text-gray-600' }
  };

  const roomsByStatus = {
    ready: rooms.filter(r => r.status === 'ready'),
    occupied: rooms.filter(r => r.status === 'occupied'),
    dirty: rooms.filter(r => r.status === 'dirty'),
    maintenance: rooms.filter(r => r.status === 'maintenance'),
    blocked: rooms.filter(r => r.status === 'blocked')
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="standalone-accommodation-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Building className="h-8 w-8 text-indigo-600" />
            <h1 className="text-2xl font-bold text-slate-900">Accommodation Dashboard</h1>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
        </div>
        <p className="text-slate-600">Manage rooms, guests, and bookings</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <Card data-testid="stat-total-rooms">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rooms</p>
                <p className="text-2xl font-bold">{stats.total_rooms || 0}</p>
              </div>
              <BedDouble className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-total-capacity">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Capacity</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total_capacity || 0}</p>
                <p className="text-[11px] text-gray-400">beds</p>
              </div>
              <BedDouble className="h-8 w-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-allocated-beds">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Allocated</p>
                <p className="text-2xl font-bold text-blue-600">{stats.allocated_beds || 0}</p>
                <p className="text-[11px] text-gray-400">beds</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-available-beds">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Available</p>
                <p className="text-2xl font-bold text-green-600">{stats.available_beds ?? 0}</p>
                <p className="text-[11px] text-gray-400">beds</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-occupancy-rate">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Occupancy</p>
                <p className="text-2xl font-bold text-blue-600">{stats.occupancy_rate || 0}%</p>
                <p className="text-[11px] text-gray-400">bed-level</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('rooms')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Dirty</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.dirty_rooms || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Check-ins</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.today_check_ins || 0}</p>
              </div>
              <LogIn className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="rooms" className="flex items-center gap-2">
            <BedDouble className="h-4 w-4" />
            <span className="hidden sm:inline">Rooms</span>
          </TabsTrigger>
          <TabsTrigger value="bookings" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Bookings</span>
          </TabsTrigger>
          <TabsTrigger value="guests" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Guests</span>
          </TabsTrigger>
          <TabsTrigger value="staff" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Staff</span>
          </TabsTrigger>
          <TabsTrigger value="damage" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">Damage & Inspections</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setShowQuickCheckIn(true)} data-testid="quick-checkin-btn">
              <LogIn className="h-4 w-4 mr-2" />
              Quick Check-in
            </Button>
            <Button variant="outline" onClick={() => setShowQuickCheckOut(true)} data-testid="quick-checkout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Quick Check-out
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('bookings')}>
              <Calendar className="h-4 w-4 mr-2" />
              New Booking
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('rooms')}>
              <BedDouble className="h-4 w-4 mr-2" />
              View Rooms
            </Button>
          </div>

          {/* Today's Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Expected Arrivals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <LogIn className="h-5 w-5 text-green-600" />
                  Expected Arrivals ({stats.expected_arrivals || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeBookings.filter(b => b.status === 'confirmed').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No arrivals expected today</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {activeBookings.filter(b => b.status === 'confirmed').slice(0, 5).map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{booking.guest_name || 'Guest'}</p>
                          <p className="text-sm text-gray-500">
                            Room {booking.room_number} • {booking.building_name}
                          </p>
                        </div>
                        <Button size="sm">Check In</Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expected Departures */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <LogOut className="h-5 w-5 text-red-600" />
                  Expected Departures ({stats.expected_departures || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeBookings.filter(b => b.status === 'checked_in').length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No departures expected today</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {activeBookings.filter(b => b.status === 'checked_in').slice(0, 5).map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{booking.guest_name || 'Guest'}</p>
                          <p className="text-sm text-gray-500">
                            Room {booking.room_number} • {booking.building_name}
                          </p>
                        </div>
                        <Button size="sm" variant="outline">Check Out</Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Room Status Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BedDouble className="h-5 w-5" />
                Room Status Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {roomsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading rooms...</div>
              ) : rooms.length === 0 ? (
                <div className="text-center py-8">
                  <BedDouble className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">No rooms configured yet</p>
                  <Button onClick={() => setActiveTab('settings')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Building & Rooms
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(statusConfig).map(([status, config]) => {
                    const Icon = config.icon;
                    const count = roomsByStatus[status]?.length || 0;
                    return (
                      <div
                        key={status}
                        className={`p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-shadow ${
                          count > 0 ? 'border-gray-200' : 'border-gray-100 opacity-50'
                        }`}
                        onClick={() => setActiveTab('rooms')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-2 rounded-full ${config.color} text-white`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className="font-medium">{config.label}</span>
                        </div>
                        <p className={`text-3xl font-bold ${config.textColor}`}>{count}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rooms Tab */}
        <TabsContent value="rooms">
          <RoomsManagement />
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings">
          <BookingsManagement />
        </TabsContent>

        {/* Guests Tab */}
        <TabsContent value="guests">
          <GuestsManagement />
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff">
          <HousingStaffManager />
        </TabsContent>

        {/* Damage & Inspections Tab */}
        <TabsContent value="damage">
          <DamageReporting />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Tabs defaultValue="buildings">
            <TabsList className="mb-4">
              <TabsTrigger value="buildings">
                <Building className="h-4 w-4 mr-2" />
                Buildings
              </TabsTrigger>
              <TabsTrigger value="room-types">
                <BedDouble className="h-4 w-4 mr-2" />
                Room Types
              </TabsTrigger>
            </TabsList>
            <TabsContent value="buildings">
              <BuildingsManagement />
            </TabsContent>
            <TabsContent value="room-types">
              <Card>
                <CardHeader>
                  <CardTitle>Room Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['Single', 'Double', 'Triple', 'Quad', 'Dorm', 'Suite', 'Apartment', 'VIP'].map((type) => (
                      <div key={type} className="p-4 border rounded-lg text-center">
                        <BedDouble className="h-8 w-8 mx-auto mb-2 text-indigo-500" />
                        <p className="font-medium">{type}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Quick Check-in Dialog */}
      <Dialog open={showQuickCheckIn} onOpenChange={setShowQuickCheckIn}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Check-in</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={(e) => {
            e.preventDefault();
            if (!checkInForm.guest_name || !checkInForm.room_id || !checkInForm.check_out_date) {
              toast.error('Please fill in all required fields');
              return;
            }
            quickCheckInMutation.mutate(checkInForm);
          }}>
            <div>
              <label className="text-sm font-medium">Guest Name *</label>
              <Input 
                placeholder="Full name" 
                data-testid="input-guest-name"
                value={checkInForm.guest_name}
                onChange={(e) => setCheckInForm({...checkInForm, guest_name: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input 
                  placeholder="+1234567890"
                  value={checkInForm.phone}
                  onChange={(e) => setCheckInForm({...checkInForm, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">ID Number</label>
                <Input 
                  placeholder="Passport/ID"
                  value={checkInForm.id_number}
                  onChange={(e) => setCheckInForm({...checkInForm, id_number: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Room *</label>
              <Select 
                value={checkInForm.room_id}
                onValueChange={(v) => setCheckInForm({...checkInForm, room_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select available room" />
                </SelectTrigger>
                <SelectContent>
                  {roomsByStatus.ready.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.building_name} - Room {room.room_number} ({room.room_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Check-out Date *</label>
                <Input 
                  type="date"
                  value={checkInForm.check_out_date}
                  onChange={(e) => setCheckInForm({...checkInForm, check_out_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Purpose</label>
                <Select 
                  value={checkInForm.purpose}
                  onValueChange={(v) => setCheckInForm({...checkInForm, purpose: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guest">Guest</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuickCheckIn(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={quickCheckInMutation.isPending} data-testid="submit-quick-checkin">
                {quickCheckInMutation.isPending ? 'Processing...' : 'Check In Guest'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Check-out Dialog */}
      <Dialog open={showQuickCheckOut} onOpenChange={setShowQuickCheckOut}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Check-out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Select a guest to check out:</p>
            {checkedInBookings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No guests currently checked in</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {checkedInBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 ${selectedBookingForCheckOut === booking.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setSelectedBookingForCheckOut(booking.id)}
                    data-testid={`checkout-booking-${booking.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{booking.guest_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Room {booking.room_number} • {booking.building_name}
                        </p>
                      </div>
                      <Badge variant="outline">{booking.purpose || 'Guest'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuickCheckOut(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => selectedBookingForCheckOut && quickCheckOutMutation.mutate(selectedBookingForCheckOut)}
                disabled={!selectedBookingForCheckOut || quickCheckOutMutation.isPending}
                data-testid="submit-quick-checkout"
              >
                {quickCheckOutMutation.isPending ? 'Processing...' : 'Check Out Guest'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandaloneAccommodationDashboard;
